import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import Select from 'react-dropdown-select'

import styled from 'styled-components'

const SelectOption = styled.div`
    padding: 10px 20px;

    &:hover {
        background-color: #eee;
    }
`

const LozengeButton = styled.button`
    background-color: white;
    height: 50px;
    min-width: 100px;
    border-radius: 0;
    margin-bottom: 15px;
    font-size: 14px;
    width: calc(33.333% - 12px);

    &:hover {
        background-color: #eee;
    }
`

const SwatchButton = styled.button`
    background-size: cover;
    background-position: center;
    height: 50px;
    width: 50px;
    border: 1px solid;
    box-shadow: inset 0 0 0 5px #fff;
    border-radius: 50%;
    font-size: 14px;
    cursor: pointer;

    &:active {
        box-shadow: inset 0 0 0 5px #ddd;
    }

    &:focus,
    &:disabled {
        box-shadow: inset 0 0 0 5px #fff;
    }

    &:hover {
        box-shadow: inset 0 0 0 5px #eee;
    }
`

const getBackgroundValue = value => {
    if (!value) {
        return null
    } else if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl')) {
        return value
    } else {
        return `url(${value})`
    }
}

export const ProductVariantOptions = props => {
    const {
        code,
        containerProps,
        swatchButtonProps,
        name,
        type,
        values,
        onSelectOption = ({ code, value }) => {
            console.log(`Selecting: ${code} - ${value}`)
        },
    } = props

    if (values?.length === 0) {
        return null
    }

    const selectedValue = values.find(v => v.selected)

    return (
        <div className="!flex !flex-col !items-start !mb-4" data-testid="product-variant-options">
            <p>{name}</p>

            {type === 'select' && (
                <div className="!w-full" data-testid="product-variant-options__select">
                    <Select
                        options={values.map(value => ({
                            label: value.displayValue,
                            value: value.identifier,
                            compatible: value.compatible,
                        }))}
                        style={{
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            borderRadius: 0,
                            height: '50px',
                            textAlign: 'left',
                        }}
                        values={
                            selectedValue
                                ? [
                                      {
                                          label: selectedValue.displayValue,
                                          value: selectedValue.identifier,
                                          compatible: true,
                                      },
                                  ]
                                : []
                        }
                        onChange={selectedValue => {
                            if (selectedValue[0]?.value && typeof onSelectOption === 'function') {
                                onSelectOption({
                                    code,
                                    value: selectedValue[0].value,
                                    isCompatible:
                                        values.find(
                                            val => val.identifier === selectedValue[0].value
                                        )?.compatible ?? true,
                                })
                            }
                        }}
                        contentRenderer={() => (
                            <p className="!mb-0" style={{ opacity: selectedValue ? 1 : 0.3 }}>
                                {selectedValue ? selectedValue.displayValue : 'Please Select...'}
                            </p>
                        )}
                        itemRenderer={({ item, methods }) => (
                            <SelectOption
                                onClick={() => methods.addItem(item)}
                                style={{
                                    opacity: item.compatible ? 1 : 0.3,
                                }}
                            >
                                <p className="!m-0 !font-bold">{item.label}</p>
                            </SelectOption>
                        )}
                        dropdownHandleRenderer={({ state }) => {
                            return (
                                <FontAwesomeIcon
                                    icon={state.dropdown ? faChevronUp : faChevronDown}
                                />
                            )
                        }}
                    />
                </div>
            )}

            {type === 'lozenge' && (
                <div
                    className="!flex flex-direction-row !justify-start !flex-wrap !w-full"
                    data-testid={`product-variant-options__lozenge`}
                >
                    {values.map((value, idx) => (
                        <LozengeButton
                            {...(typeof onSelectOption === 'function' && {
                                onClick: e =>
                                    onSelectOption({
                                        code,
                                        value: value.identifier,
                                        isCompatible: value.compatible,
                                    }),
                            })}
                            style={{
                                border: `1px solid ${value.selected ? '#333' : '#ddd'}`,
                                marginRight: (idx + 1) % 3 === 0 ? '0' : '15px',
                                opacity: value.compatible ? 1 : 0.3,
                            }}
                            key={`product-variant-options__lozenge-${idx}`}
                            data-testid={`product-variant-options__lozenge-${idx}`}
                        >
                            {value.displayValue}
                        </LozengeButton>
                    ))}
                </div>
            )}

            {type === 'swatch' && (
                <div
                    className="!flex !flex-row !flex-wrap !w-full"
                    data-testid="product-variant-options__swatch"
                >
                    {values.map((value, idx) => (
                        <div
                            className="!flex !flex-col !justify-center !items-center"
                            key={`${value.identifier}_${idx}`}
                            style={{
                                marginRight: idx === values.length - 1 ? '0' : '15px',
                                marginBottom: '15px',
                                opacity: value.compatible ? 1 : 0.6,
                            }}
                        >
                            <SwatchButton
                                title={value.displayValue}
                                style={{
                                    background: getBackgroundValue(
                                        value.altValue || value.displayValue
                                    ),
                                    borderColor: value.selected ? '#333' : '#ddd',
                                    opacity: value.compatible ? 1 : 0.6,
                                    marginBottom: swatchButtonProps?.showButtonLabel ? '5px' : 0,
                                }}
                                {...(typeof onSelectOption === 'function' && {
                                    onClick: () =>
                                        onSelectOption({
                                            code,
                                            value: value.identifier,
                                            isCompatible: value.compatible,
                                        }),
                                })}
                                aria-label={value.displayValue}
                                data-testid={`product-variant-options__swatch-${idx}`}
                            >
                                <span style={{ opacity: 0 }}>{value.displayValue}</span>
                            </SwatchButton>

                            {swatchButtonProps?.showButtonLabel && (
                                <span
                                    style={{
                                        fontSize: '12px',
                                    }}
                                    data-testid={`product-variant-options__swatch-${idx}-label`}
                                >
                                    {value.displayValue}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
